import React, { useEffect, useMemo, useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import { convertTo12HourFormat, formatToReadableDate } from 'utils/dateTimeHelpers';
import type { DayOfWeekType } from 'types';
import { DOSHII_MENU_NAME_INDEX } from 'consts';
import classNames from 'classnames';
import { useOperatingHours } from 'utils/useOperatingHours';
import { type OperatingHours, StatusEnum, type Venue, type Menu } from '../../generated/graphql';
import CategoryList from '../CategoryList/CategoryList';
import Tabs from '../Tabs/Tabs';
import { splitMenu } from './splitMenu';
import ExpandControls from '../SingleMenu/ExpandControls';
import styles from './DoshiiMenuLogic.module.scss';
import useScreenWidthQuery from '../../common/Hooks/UseScreenWidthQuery';
import { WindowBreakpoints } from '../../utils/DocumentHelper';
import { logAmplitudeEvent, AmplitudeEventCategories, AmplitudeEventProperties } from '../../utils/Amplitude';

const OPERATING_HOURS_FEATURE_FLAG = 'operatingHoursEnabled';

const TodayHours = ({
  allTodayHours,
  todayClosures
}: {
  allTodayHours: DayOfWeekType | null;
  todayClosures: OperatingHours | null;
}): JSX.Element | null => {
  if (!allTodayHours) {
    return null;
  }
  if (
    !allTodayHours.hours?.status ||
    allTodayHours.hours.status !== StatusEnum.Open ||
    !allTodayHours.hours.time.length
  ) {
    return <h3>Closed today</h3>;
  }

  if (todayClosures?.dates) {
    if (todayClosures.dates[0] && !todayClosures.dates[1]) {
      return (
        <h3>
          Closed on{' '}
          {formatToReadableDate({
            day: todayClosures.dates[0].day,
            month: todayClosures.dates[0].month,
            year: todayClosures.dates[0].year
          })}
        </h3>
      );
    }
    if (todayClosures.dates[1]) {
      return (
        <h3>
          Closed up to{' '}
          {formatToReadableDate({
            day: todayClosures.dates[1].day,
            month: todayClosures.dates[1].month,
            year: todayClosures.dates[1].year
          })}
        </h3>
      );
    }
  }

  return (
    <div className={styles.TodayHours}>
      <h3>Open today</h3>
      <div className={classNames(styles.TimeRanges, { [styles.StrikeThrough]: todayClosures })}>
        {allTodayHours?.hours?.time?.length > 0
          ? allTodayHours.hours.time
              .map(({ from, to }) => `${convertTo12HourFormat(from)} - ${convertTo12HourFormat(to)}`)
              .join(', ')
          : 'No hours available'}
      </div>
    </div>
  );
};

type Props = {
  draftMenu: Menu;
  themeClassName?: string | null;
  currentVenue?: Venue;
  collapsedCategories: string[];
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
};

const DoshiiMenuLogic: React.FC<Props> = ({
  draftMenu,
  currentVenue,
  themeClassName,
  collapsedCategories,
  handleCollapseAll,
  handleExpandAll
}) => {
  const params = useParams();
  const defaultName = draftMenu?.apps[0].brand?.name ?? currentVenue?.name ?? '';
  const categories = draftMenu.categories || [];
  const products = draftMenu.products || [];
  const apps = draftMenu.apps || [];

  const uncategorisedCategory = categories.find(({ mmId }) => mmId === 'UNCATEGORISED');
  const menus = splitMenu({
    venueName: defaultName,
    categories: categories.filter(({ mmId }) => mmId !== 'UNCATEGORISED')
  });
  const tabIdNameMap = useMemo(
    () =>
      menus.reduce((tabs, tab) => {
        tabs[tab.id] = tab.displayName;
        return tabs;
      }, {} as Record<string, string>),
    [menus]
  );
  const [selectedTab, setSelectedTab] = useState<string>(menus[0]?.id ?? defaultName);

  useEffect(() => {
    // Get doshii menu after /hours in the URL
    const doshiiMenuInUrl = params['*']?.startsWith('hours/')
      ? decodeURIComponent(params['*']).replace(/^hours\//, '')
      : null;

    // Set the selected tab to the menu name in the URL
    // This is done to ensure the correct menu is selected on the tab when user navigates to the hours side panel
    if (doshiiMenuInUrl) {
      setSelectedTab(`doshii:menu:${doshiiMenuInUrl}`);
    }
  }, [params]);

  const doshiiMenu = useMemo(() => {
    const selectedMenu = selectedTab !== defaultName ? selectedTab : undefined;
    return selectedMenu?.split(':')[DOSHII_MENU_NAME_INDEX];
  }, [selectedTab, defaultName]);

  const {
    todayMMHours,
    todayClosures,
    isOperatingHoursSupported,
    commonHoursAcrossAllMenus,
    hasCommonHoursAcrossAllMenusBeenApplied
  } = useOperatingHours({
    currentVenue,
    appId: apps[0].id,
    doshiiMenu
  });

  const [data, loading, error] = useFeatureFlagsQuery([OPERATING_HOURS_FEATURE_FLAG]);

  const isOperatingHoursFeatureEnabled = useMemo(() => {
    if (loading || error || !data) {
      return false;
    }
    const operatingHoursFeatureFlag = data[OPERATING_HOURS_FEATURE_FLAG];
    return !!operatingHoursFeatureFlag?.isEnabled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [innerWidth] = useScreenWidthQuery();
  const isDesktop = innerWidth > WindowBreakpoints.SM;

  const selectedMenu = menus.find((menu) => menu.id === selectedTab) || {
    displayName: tabIdNameMap[selectedTab],
    usesDoshiiMenuLogic: selectedTab !== defaultName,
    categories: []
  };

  const logHoursLinkClick = (): void => {
    logAmplitudeEvent({
      name: `click Menu Hours`,
      category: AmplitudeEventCategories.MM_MENU,
      properties: {
        [AmplitudeEventProperties.VENUE_ID]: currentVenue?.hashedId || '',
        [AmplitudeEventProperties.APP]: apps[0].name,
        [AmplitudeEventProperties.MENU_NAME]: doshiiMenu || ''
      }
    });
  };

  return (
    <div>
      {Object.keys(tabIdNameMap).length > 1 && (
        <div className={`container ${styles.Tabs}`}>
          <Tabs tabs={tabIdNameMap} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </div>
      )}

      {isOperatingHoursFeatureEnabled && isOperatingHoursSupported && (
        <div className="container">
          {!hasCommonHoursAcrossAllMenusBeenApplied ? (
            <div className={`${styles.Hours} ${themeClassName ? `Theme${themeClassName}` : ''}`}>
              <TodayHours allTodayHours={todayMMHours} todayClosures={todayClosures} />
              <Link
                to={
                  commonHoursAcrossAllMenus
                    ? `/venue/${currentVenue?.hashedId}/hours`
                    : `hours/${encodeURIComponent(doshiiMenu || '')}`
                }
                data-testid="manage-hours-link"
                onClick={logHoursLinkClick}
              >
                <strong>Manage {commonHoursAcrossAllMenus ? 'menu hours' : "this menu's hours"}</strong>
                <i className="mm-angle-right" />
              </Link>
            </div>
          ) : (
            <div className={styles.Alert}>
              <div className={styles.Copy}>
                <i className="mm-error-circle" />
                Menu hours need to be reviewed and applied before publishing.
              </div>
              <Link
                className={styles.Link}
                to={`/venue/${currentVenue?.hashedId}/hours`}
                data-testid="manage-hours-link"
                onClick={logHoursLinkClick}
              >
                Manage menu hours
              </Link>
            </div>
          )}
        </div>
      )}

      {isDesktop && (
        <ExpandControls
          isAllExpanded={!collapsedCategories.length}
          isAllCollapsed={collapsedCategories.length === categories.length}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
        />
      )}
      <CategoryList
        categories={
          uncategorisedCategory ? [...selectedMenu.categories, uncategorisedCategory] : selectedMenu.categories
        }
        products={products}
        menuName={tabIdNameMap[selectedTab]}
        usesDoshiiMenuLogic={selectedMenu.usesDoshiiMenuLogic}
      />
    </div>
  );
};

export default DoshiiMenuLogic;
